import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Warning as WarningType, Warnings } from '~/hooks/useEntityWarnings';
import Warning from '~/components/organism/Warning';
import Icon from '~/components/atom/Icon';
import useClickOutside from '~/hooks/useClickOutside';

const SmallScreenWarningsContainer: React.FCC<{
  warnings: Warnings;
  snooze: (warning: WarningType) => void;
}> = ({ warnings, snooze }) => {
  const mobileWrapperRef = useRef(null);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  useClickOutside({
    allowClickInRefs: [mobileWrapperRef],
    callback: () => setMobileExpanded(false),
  });

  return (
    <>
      <MobileBadgeWrapper ref={mobileWrapperRef}>
        <BadgeButton
          onClick={() => setMobileExpanded(prev => !prev)}
          aria-label={`${warnings.length} waarschuwingen`}
        >
          <Icon name="exclamation" color={{ group: 'white' }} />
          <BadgeCount>{warnings.length}</BadgeCount>
        </BadgeButton>
      </MobileBadgeWrapper>
      {mobileExpanded && (
        <MobileWarningsWrapper>
          {warnings.map(warning => (
            <WarningContainer key={warning.hash}>
              <Warning onClose={snooze} warning={warning} />
            </WarningContainer>
          ))}
        </MobileWarningsWrapper>
      )}
    </>
  );
};

const WarningContainer = styled.div`
  width: 100%;
`;

// Approximate values for the HamburgerButton
const HAMBURGER_BUTTON_WIDTH = 40;
const HAMBURGER_BUTTON_HEIGHT = 40;

const MobileBadgeWrapper = styled.div(
  ({ theme }) => css`
    width: 100%;
    position: fixed;
    top: ${theme.space('s')};
    left: calc(${theme.space('xxs')} + ${HAMBURGER_BUTTON_WIDTH}px);
    z-index: ${theme.z('top') + 2};
    pointer-events: all;
  `,
);

const MobileWarningsWrapper = styled.div(
  ({ theme }) => css`
    top: ${HAMBURGER_BUTTON_HEIGHT * 2}px;
    left: ${theme.space('xxs')};
    width: calc(100% - ${theme.space('xxs')} * 2);
    position: fixed;
    z-index: ${theme.z('top') + 2};
    pointer-events: all;
  `,
);

const BadgeButton = styled.button(
  ({ theme }) => css`
    border: none;
    background: none;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    cursor: pointer;
    font-size: ${theme.fs('l')};
  `,
);

const BadgeCount = styled.span(
  ({ theme }) => css`
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${theme.color('danger')};
    color: ${theme.color('white')};
    font-size: ${theme.fontSize('s')};
    font-weight: ${theme.fontWeight('bold')};
    border-radius: ${theme.getTokens().border.radius.full};
    padding: 0 4px;
  `,
);

export default SmallScreenWarningsContainer;
